import {ChangeEvent, useEffect, useMemo, useState} from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { BsCalendar, BsFillGeoAltFill } from 'react-icons/bs';
import { FaArrowLeft } from 'react-icons/fa6';
import { To, useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../../components/generic';
import { IconLink } from '../../../../../../components/iconLink/IconLink';
import ConfirmationModal from '../../../../../../components/modals/ConfirmationModal';
import StatusIcon from '../../../../../../components/status/StatusIcon';
import { LinkCell } from '../../../../../../components/tables/cellComponents/LinkCell';
import { useToast } from '../../../../../../context';
import {
  buildCoursePdfFileName,
  openPdfPreview
} from '../../../../../../services/api';
import { useGetThemeQuery } from '../../../../../../services/endpoints/content/theme';
import {
  useGetCourseQuery,
  useSendReviewRequestsMutation,
  useSendTextReminderMutation,
  useSendThankEmailMutation,
  useSendWelcomeEmailMutation,
  useUpdateEmailsMutation
} from '../../../../../../services/endpoints/schedule/course';
import {
  useCreateSubRequestMutation,
  useDeleteSubRequestMutation,
  useRenegClassMutation
} from '../../../../../../services/endpoints/schedule/lessonAssignment';
import { getCurrentUser } from '../../../../../../services/helper';
import { Course } from '../../../../../../types/course.type';
import { Lesson } from '../../../../../../types/lesson.type';
import { displaySessionTimeRange } from '../../../../../../utils/dateUtils';
import { normalizeThemeLessonPlans } from '../../../../../../utils/lessonPlanUtils';
import { getErrorMessage } from '../../../../../../utils/utils';
import '../SessionDetail.css';
import { userCanEditCreateDelete_Sessions, userCanGenerateInvoice_Sessions } from '../SessionsView';
import { SessionContactsSection } from './components/SessionContactsSection';
import { SessionDetailsSection } from './components/SessionDetailsSection';
import { SessionInvoicesSection } from './components/SessionInvoicesSection';
import { SessionNotesSection } from './components/SessionNotesSection';
import { SessionReceiptsSection } from './components/SessionReceiptsSection';
import { SessionResourcesSection } from './components/SessionResourcesSection';
import { lessonAssignmentActionFn, lessonAssignmentActionsData } from './SessionDetail.types';
import { SessionDetailAccordion } from './SessionDetailAccordion';

// Re-export types for consumers that previously imported from SessionDetail.tsx
export type {
  lessonAssignmentActionFn,
  lessonAssignmentActionsData,
  SessionDocument
} from './SessionDetail.types';

const currentUser = getCurrentUser();

const isOwnSubRequest = (requestingInstructorId?: number) =>
  Number(requestingInstructorId) === Number(currentUser?.id);

export const SessionDetail = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { data, error, isLoading } = useGetCourseQuery(+params.id!, {
    refetchOnMountOrArgChange: true
  });
  const { data: theme } = useGetThemeQuery(data?.course.theme_id ?? 0, {
    skip: !data?.course.theme_id
  });

  const [renegSubOffer] = useRenegClassMutation();
  const [createSubRequest, { isLoading: isCreatingSubRequest }] = useCreateSubRequestMutation();
  const [deleteSubRequest, { isLoading: isDeletingSubRequest }] = useDeleteSubRequestMutation();
  const [sendWelcomeEmail] = useSendWelcomeEmailMutation();
  const [sendTextReminder] = useSendTextReminderMutation();
  const [sendThankEmail] = useSendThankEmailMutation();
  const [sendReviewRequests, { isLoading: isSendingReviewRequests }] = useSendReviewRequestsMutation();
  const [updateEmails, { isLoading: isUpdatingEmails }] = useUpdateEmailsMutation();

  const [course, setCourse] = useState<Course>();
  const [sortedLessons, setSortedLessons] = useState<Lesson[]>([]);
  const [rosterEmails, setRosterEmails] = useState<string>('');

  const [showCancelSubRequestModal, setShowCancelSubRequestModal] = useState(false);
  const [subRequestToDelete, setSubRequestToDelete] = useState<lessonAssignmentActionsData>();
  const [showCreateSubRequestModal, setShowCreateSubRequestModal] = useState(false);
  const [subRequestToCreate, setSubRequestToCreate] = useState<lessonAssignmentActionsData>();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [welcomeEmailTarget, setWelcomeEmailTarget] = useState<string>('');
  const [showReviewRequestModal, setShowReviewRequestModal] = useState(false);

  const featuredTlps = useMemo(() => normalizeThemeLessonPlans(theme?.theme), [theme?.theme]);

  useEffect(() => {
    if (error && !isLoading) addToast('Error while loading session', 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setCourse(data.course);
      setSortedLessons(
        [...(data.course.lessons ?? [])].sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        )
      );

      // Use saved emails if present; otherwise auto-populate from registered children's parent emails
      if (data.course.emails) {
        setRosterEmails(data.course.emails);
      } else {
        const parentEmails = (data.course.registrations ?? [])
          .map((r: any) => r?.child?.parent?.email)
          .filter(Boolean);
        const unique = [...new Set<string>(parentEmails)];
        setRosterEmails(unique.join(', '));
      }
    }
  }, [data]);

  const handleUpdateEmails = async () => {
    if (!data?.course?.id) {
      addToast('Unable to update emails: missing course id', 'error');
      return;
    }
    try {
      await updateEmails({ id_course: data.course.id, emails: rosterEmails }).unwrap();
      addToast('Emails were updated.', 'success');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleWelcomeEmailClick = (email: string) => {
    setWelcomeEmailTarget(email);
    setShowWelcomeModal(true);
  };

  const handleConfirmWelcomeEmail = () => {
    if (!course?.id || !welcomeEmailTarget) return;
    sendWelcomeEmail({ courseId: course.id, recipient: welcomeEmailTarget })
      .unwrap()
      .then(() => {
        addToast('The session welcome email has been sent.', 'success');
        setShowWelcomeModal(false);
      })
      .catch((err) => {
        addToast(getErrorMessage(err), 'error');
        setShowWelcomeModal(false);
      });
  };

  const handleConfirmReviewRequests = () => {
    if (!course?.id) return;
    sendReviewRequests(course.id)
      .unwrap()
      .then(({ sent, skipped, failed }) => {
        addToast(
          `Review requests sent: ${sent}` +
            (skipped ? `, skipped: ${skipped}` : '') +
            (failed ? `, failed: ${failed}` : ''),
          sent ? 'success' : 'error'
        );
        setShowReviewRequestModal(false);
      })
      .catch((err) => {
        addToast(getErrorMessage(err), 'error');
        setShowReviewRequestModal(false);
      });
  };

  // Unique parent emails on the roster; the API applies the final filtering (paid, active registrations)
  const reviewRequestParentCount = new Set(
    (course?.registrations ?? [])
      .map((r: any) => r?.child?.parent?.email?.toLowerCase().trim())
      .filter(Boolean)
  ).size;

  const handleSendTextReminder = async (type: string) => {
    if (!data?.course?.id) return;
    if (type === 'review-request') {
      setShowReviewRequestModal(true);
      return;
    }
    try {
      if (type === 'welcome-course') {
        await sendWelcomeEmail({ courseId: data.course.id, bulk: true }).unwrap();
        addToast('Welcome email sent.', 'success');
      } else if (type === 'thank-course') {
        await sendThankEmail({ courseId: data.course.id, bulk: true }).unwrap();
        addToast('Thank you email sent.', 'success');
      } else if (type === 'remind') {
        await sendTextReminder({ id: data.course.id, type }).unwrap();
        addToast('Roster/Waiver sent.', 'success');
      } else if (type === 'send-text-reminder') {
        await sendTextReminder({ id: data.course.id, type }).unwrap();
        addToast('SMS was sent.', 'success');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handlePDFClick = (mode: string) => {
    if (!data?.course?.id) return;
    const fileName = buildCoursePdfFileName(mode, data.course.slug);
    openPdfPreview(
        `courses/generate-course/${data.course.id}/${encodeURIComponent(fileName)}?mode=${encodeURIComponent(mode)}`
    ).catch((e) => addToast(getErrorMessage(e), 'error'));
  };

  const actionCallback: lessonAssignmentActionFn = (action, actionData) => {
    switch (action) {
      case 'reneg':
        if (actionData.id) renegSubOffer({ id: actionData.id }).unwrap().catch(console.error);
        break;
      case 'create':
        setSubRequestToCreate(actionData);
        setShowCreateSubRequestModal(true);
        break;
      case 'delete':
        setSubRequestToDelete(actionData);
        setShowCancelSubRequestModal(true);
        break;
    }
  };

  const waitlistEmails =
    data?.course?.course_waitlist_emails
      ?.split(',')
      .map((e) => e.trim())
      .map((e, i, arr) => (i < arr.length - 1 ? `${e},` : e))
      .join('\n') ?? '';

  if (isLoading) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  const venueMapsUrl = (() => {
    if (!course?.venue) return null;
    const parts = [
      course.venue.address,
      course.venue.city,
      course.venue.state,
      course.venue.zip
    ].filter(Boolean);
    const fullAddress = parts.join(', ');
    return fullAddress
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
      : null;
  })();

  const venueAddress = (
    <a
      href={venueMapsUrl ?? '#'}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        if (!venueMapsUrl) e.preventDefault();
      }}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Stack
        direction="horizontal"
        gap={2}
        className="text-info py-1"
      >
        <BsFillGeoAltFill size={24} />
        <span className="text-info fw-semibold">{course?.venue?.title}</span>
      </Stack>
      <Stack
        className="text-info fw-semibold"
        style={{ marginLeft: 34 }}
      >
        <span>{course?.venue?.address}</span>
        <span>{`${course?.venue?.city} ${course?.venue?.state}, ${course?.venue?.zip}`}</span>
      </Stack>
    </a>
  );

  const chefPay =
    course?.pay_type === 'flat_rate' ? `Flat Rate $${course?.flat_rate}/day` : 'Hourly Rate';

  // Time display: render in location's timezone, replicating v2's FancyTime
  // convention. Lesson-level starts_at/ends_at are nearly always placeholders
  // ("12:00 AM"), so we use course-level instead. See displaySessionTimeRange
  // for math details.
  const tz = (course?.venue as any)?.location?.timezone;
  const timeDisplay = displaySessionTimeRange(course?.starts_at, course?.ends_at, tz) || null;

  const accordionProps = {
    course,
    sortedLessons,
    featuredTlps,
    actionCallback,
    onWelcomeEmailClick: handleWelcomeEmailClick,
    onPDFClick: handlePDFClick,
    onSendTextReminder: handleSendTextReminder,
    rosterEmails,
    waitlistEmails,
    isUpdatingEmails,
    onRosterEmailChange: (e: ChangeEvent<HTMLInputElement>) => setRosterEmails(e.target.value),
    onUpdateEmails: handleUpdateEmails
  };

  return (
    <>
      {/* ── Top bar ── */}
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate(-1 as To)}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Sessions List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{course?.title}</h2>
        </Stack>
        <Stack
          direction="horizontal"
          gap={1}
          className="d-flex justify-content-end flex-wrap"
        >
          {userCanEditCreateDelete_Sessions(currentUser) && (
            <SimpleButton
              variant="primary"
              height={36}
              onClick={() => navigate(`/admin/schedule/sessions/${course?.id}/edit`)}
            >
              Edit session
            </SimpleButton>
          )}
          {userCanGenerateInvoice_Sessions(currentUser) && (
            <SimpleButton
              variant="primary"
              height={36}
              onClick={() =>
                navigate(`/admin/schedule/sessions/invoicer/${course?.id}`, { state: { course } })
              }
            >
              Generate invoice
            </SimpleButton>
          )}
        </Stack>
      </div>

      {/* ── Mobile layout ── */}
      <div className="d-lg-none">
        <Stack
          gap={4}
          className="p-4"
        >
          <SessionDetailAccordion {...accordionProps} />
          <DetailSection>
            <span className="fs-5 text-info fw-bold">{course?.title}</span>
            {venueAddress}
            <IconLink icon={<BsCalendar size={24} />}>
              <span className="text-secondary fw-semibold">{course?.day_of_week}</span>
            </IconLink>
            <LabelText label="Time:">{timeDisplay}</LabelText>
            <LabelText
              label="ID:"
              text={course?.id}
            />
            {!currentUser?.isInstructor && (
              <LabelText
                label="Theme:"
                children={
                  <LinkCell
                    content={course?.theme?.name ?? ''}
                    url={`/admin/content/themes/${course?.theme?.id}`}
                  />
                }
              />
            )}
            <LabelText
              label="Room:"
              text={course?.room}
            />
            <LabelText
              label="Enrollment:"
              text={course?.enrollment}
            />
            {!currentUser?.isInstructor && (
              <LabelText
                label="Session Payment type:"
                text={course?.payment_type === 'onsite' ? 'On-site' : 'Website'}
              />
            )}
            {!currentUser?.isInstructor && (
              <LabelText
                label="Tuition:"
                direction="vertical"
                text={course?.tuition_summary_string}
              />
            )}
            <LabelText
              label="Chef Pay:"
              text={chefPay}
            />
            {course?.payment_type === 'website' && (
              <LabelText label="PRORATED:">
                <StatusIcon value={course?.can_be_prorated} />
              </LabelText>
            )}
            <LabelText label="Instructors:">
              <Stack>
                {course?.course_assignments.map((ca) => (
                  <span key={ca.id}>
                    {ca.instructor?.first_name} {ca.instructor?.last_name}
                  </span>
                ))}
              </Stack>
            </LabelText>
          </DetailSection>
        </Stack>
      </div>

      {/* ── Desktop layout ── */}
      <div className="d-none d-lg-flex">
        <div
          className="w-100 mt-5 px-5 py-4"
          style={{ maxWidth: 'calc(100% - var(--sfc-dash-sidebar, 400px))' }}
        >
          <SessionDetailAccordion {...accordionProps} />
        </div>
        <Stack
            gap={4}
            className="p-4"
            style={{ width: 'var(--sfc-dash-sidebar, 400px)', background: '#F2F2F2' }}
        >
          <DetailSection title="Resources">
            <SessionResourcesSection
              course={course}
              onPDFClick={handlePDFClick}
              onSendTextReminder={handleSendTextReminder}
            />
          </DetailSection>
          <SessionDetailsSection course={course} />
          {course?.pay_type === 'flat_rate' && (
            <SessionReceiptsSection
              course={course}
              idPrefix="desktop-"
            />
          )}
          {/* Admin-only sidebar sections (contacts, invoices, notes) */}
          {!currentUser?.isInstructor && (
            <>
              <SessionContactsSection course={course} />
              <SessionInvoicesSection course={course} />
              <SessionNotesSection course={course} />
            </>
          )}
        </Stack>
      </div>

      {/* ── Modals ── */}
      {showCancelSubRequestModal && subRequestToDelete && (
        <ConfirmationModal
          show={showCancelSubRequestModal}
          title="Delete Substitute Request"
          message={`Are you sure you want to cancel ${
            subRequestToDelete.instructorName &&
            !isOwnSubRequest(subRequestToDelete.requesting_instructor_id)
              ? `${subRequestToDelete.instructorName}'s`
              : 'your'
          } sub request?`}
          confirmText="Yes"
          cancelText="No"
          onConfirm={() => {
            deleteSubRequest({ id: subRequestToDelete.id })
              .unwrap()
              .then(() => {
                addToast('Substitute request deleted successfully', 'success');
                setShowCancelSubRequestModal(false);
              })
              .catch((err) => {
                addToast(getErrorMessage(err), 'error');
                setShowCancelSubRequestModal(false);
              });
          }}
          onCancel={() => setShowCancelSubRequestModal(false)}
          isLoading={isDeletingSubRequest}
        />
      )}
      {showCreateSubRequestModal && subRequestToCreate && (
        <ConfirmationModal
          show={showCreateSubRequestModal}
          title="Request Substitute"
          message={`Are you sure you want to request a substitute${
            subRequestToCreate.instructorName &&
            !isOwnSubRequest(subRequestToCreate.requesting_instructor_id)
              ? ` for ${subRequestToCreate.instructorName}`
              : ''
          }?`}
          confirmText="Yes"
          cancelText="No"
          onConfirm={() => {
            createSubRequest({
              lesson_id: subRequestToCreate.lesson_id,
              requesting_instructor_id: subRequestToCreate.requesting_instructor_id,
              course_id: subRequestToCreate.course_id,
              location_id: subRequestToCreate.location_id
            })
              .unwrap()
              .then(() => {
                addToast('A substitute has been requested', 'success');
                setShowCreateSubRequestModal(false);
              })
              .catch((err) => {
                addToast(getErrorMessage(err), 'error');
                setShowCreateSubRequestModal(false);
              });
          }}
          onCancel={() => setShowCreateSubRequestModal(false)}
          isLoading={isCreatingSubRequest}
        />
      )}
      {showWelcomeModal && (
        <ConfirmationModal
          show={showWelcomeModal}
          title="Send Welcome Email"
          message={`Are you sure you want to manually send the session welcome email to ${welcomeEmailTarget}?`}
          confirmText="Yes"
          cancelText="No"
          onConfirm={handleConfirmWelcomeEmail}
          onCancel={() => setShowWelcomeModal(false)}
        />
      )}
      {showReviewRequestModal && (
        <ConfirmationModal
          show={showReviewRequestModal}
          title="Send Review Requests"
          message={`Send a ConsumerFusion review request email to up to ${reviewRequestParentCount} parent${
            reviewRequestParentCount === 1 ? '' : 's'
          } from this session? This can only be done once per session.`}
          confirmText="Send"
          cancelText="Cancel"
          onConfirm={handleConfirmReviewRequests}
          onCancel={() => setShowReviewRequestModal(false)}
          isLoading={isSendingReviewRequests}
        />
      )}
    </>
  );
};
